import { Meta } from './Meta';
import { HeaderFeature } from '../Header';
import { FooterFeature } from '../Footer';
import { CookieConsent } from './Meta/CookieConsent';
import { SiteImprove } from './Meta/SiteImprove';
import { SkipToMain } from 'ui/components';
import { Layout } from 'ui/components/4-habitats/Layout/Layout';
import { useRouter } from 'next/router';
import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { useEffect, useRef } from 'react';
import BackToTopFeature from 'features/Layouts/BackToTop/BackToTopFeature';

export const LayoutFeature: React.FC<Content.PageContent> = ({ children, page, siteTheme, globalScript }) => {
	const { themeBase, theme50, theme30, theme20, theme10, themeOverride } = siteTheme[0]?.content?.properties?.theme?.properties ?? {};
	const router = useRouter();
	const themeStyle = {
		'--theme-base': themeBase,
		'--theme-50': theme50,
		'--theme-30': theme30,
		'--theme-20': theme20,
		'--theme-10': theme10,
		...(themeOverride ? {
            '--color-concern': themeOverride,
            '--color-concern--dark': themeOverride,
			'--color-primary': themeOverride,
			'--color-primary--dark': themeOverride,
        } : {})
	} as React.CSSProperties;
	const dictionary = useDictionaryContext();

	const globalScriptRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!globalScript || !globalScriptRef.current) return;

		const container = globalScriptRef.current;
		const trimmed = globalScript.trim();

		container.innerHTML = "";

		const fragment = document.createRange().createContextualFragment(trimmed);
		const scripts = Array.from(fragment.querySelectorAll("script"));
		scripts.forEach((script) => script.remove());

		container.append(fragment);
		const scriptEls: HTMLScriptElement[] = [];

		scripts.forEach((oldScript) => {
			const scriptEl = document.createElement("script");

			Array.from(oldScript.attributes).forEach((attr) => {
				scriptEl.setAttribute(attr.name, attr.value);
			});

			scriptEl.text = oldScript.textContent || "";
			document.body.appendChild(scriptEl);
			scriptEls.push(scriptEl);
		});

		return () => {
			scriptEls.forEach((scriptEl) => {
				if (document.body.contains(scriptEl)) {
					document.body.removeChild(scriptEl);
				}
			});
		};
    }, [globalScript, router.asPath]);

	return (
		<>
		<Layout router={router}>
			<SkipToMain
				link="#main"
				title={dictionary.getValue('SkipToMain.AriaLabel', null, 'SkipToMain.AriaLabel')}
				ariaLabel={dictionary.getValue('SkipToMain.AriaLabel', null, 'SkipToMain.AriaLabel')}
			>
				{dictionary.getValue('SkipToMain.AriaLabel', null, 'SkipToMain.AriaLabel')}
			</SkipToMain>
			<div style={themeStyle}>
				<CookieConsent />
				<SiteImprove />

				<div ref={globalScriptRef} />

				<Meta {...page?.properties} themeColor={themeBase} />
				<HeaderFeature />
				{children}
				<FooterFeature />
			</div>
		</Layout>
		<BackToTopFeature />
		</>
	);
};
